import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

const PRODUCT_NAME = "Projects";
const DEFAULT_RE_DISPLAY_AFTER_DAYS = 7;

export interface FeedbackConfig {
  accountToken: string;
  apiHost: string;
  reDisplayAfterDays: number;
  mfeRegistryBase: string;
  mfeVersion?: string;
  product: string;
}

const FeedbackContext = createContext<FeedbackConfig | null>(null);

export function useFeedbackConfig(): FeedbackConfig | null {
  return useContext(FeedbackContext);
}

interface FeedbackConfigProviderProps {
  accountToken?: string;
  apiHost?: string;
  reDisplayAfterDays?: number;
  mfeRegistryBase?: string;
  mfeVersion?: string;
  children: ReactNode;
}

export function FeedbackConfigProvider({
  accountToken,
  apiHost,
  reDisplayAfterDays,
  mfeRegistryBase,
  mfeVersion,
  children,
}: FeedbackConfigProviderProps) {
  const value = useMemo<FeedbackConfig | null>(() => {
    if (!accountToken || !apiHost || !mfeRegistryBase) {
      if (accountToken && apiHost) {
        console.warn(
          "[FeedbackConfigProvider] mfeRegistryBase is missing from product.settings; the feedback collector will not render.",
        );
      }
      return null;
    }
    return {
      accountToken,
      apiHost,
      reDisplayAfterDays: reDisplayAfterDays ?? DEFAULT_RE_DISPLAY_AFTER_DAYS,
      mfeRegistryBase,
      mfeVersion,
      product: PRODUCT_NAME,
    };
  }, [accountToken, apiHost, reDisplayAfterDays, mfeRegistryBase, mfeVersion]);

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
}
